/**
 * Direction primitives for the things CSS logical properties cannot fix.
 *
 * Tailwind's logical utilities handle flow layout. They do NOT handle:
 *   - the floor-plan canvas, which is absolute-positioned pixel geometry and
 *     must MIRROR horizontally in RTL;
 *   - the timeline grid, whose time axis must run right-to-left in RTL;
 *   - drag deltas, which have to be un-mirrored before they become an x
 *     coordinate to store.
 *
 * Those three live here so Phase 5 has one place to be correct.
 */

import { useSyncExternalStore } from 'react';

export type Direction = 'ltr' | 'rtl';

function currentDirection(): Direction {
  return document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
}

/**
 * Subscribe to <html dir> changes. i18n.ts writes the attribute; this makes
 * canvas code re-render when it flips, without threading a prop everywhere.
 */
function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });
  return () => observer.disconnect();
}

export function useDirection(): Direction {
  return useSyncExternalStore(subscribe, currentDirection, () => 'ltr');
}

export function useIsRtl(): boolean {
  return useDirection() === 'rtl';
}

/**
 * Mirror an x coordinate within a container.
 *
 * A table stored at x=120 in a 900px-wide room renders at 900 - 120 - width in
 * RTL. Store coordinates ONCE, in LTR space, always. Mirroring is a render-time
 * concern; if it leaks into the database, a venue that switches language once
 * corrupts its own floor plan.
 */
export function mirrorX(x: number, elementWidth: number, containerWidth: number, dir: Direction): number {
  return dir === 'rtl' ? containerWidth - x - elementWidth : x;
}

/** Un-mirror a drag result before persisting it. The inverse of `mirrorX`. */
export function unmirrorX(
  renderedX: number,
  elementWidth: number,
  containerWidth: number,
  dir: Direction,
): number {
  return dir === 'rtl' ? containerWidth - renderedX - elementWidth : renderedX;
}

/**
 * Rotation also mirrors: a table angled at 30 degrees must lean the other way
 * in a mirrored room, or the floor plan stops matching the actual restaurant.
 */
export function mirrorRotation(degrees: number, dir: Direction): number {
  return dir === 'rtl' ? (360 - degrees) % 360 : degrees;
}

/**
 * Fraction along the time axis, in render space.
 *
 * `progress` is 0 at the start of the service period and 1 at the end. In RTL
 * the axis runs right to left, so the same instant sits at `1 - progress`.
 */
export function timeAxisFraction(progress: number, dir: Direction): number {
  const clamped = Math.min(1, Math.max(0, progress));
  return dir === 'rtl' ? 1 - clamped : clamped;
}

/** The inline-start edge in render space, as a percentage string. */
export function inlineStartPercent(progress: number, dir: Direction): string {
  return `${timeAxisFraction(progress, dir) * 100}%`;
}
